import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Card, Table, Button, Modal, Input, Select, FormStack, Alert, Badge, Text } from "@marekl/adminer/components";
import {
  useEshopProductMapList,
  useCreateEshopProductMap,
  useUpdateEshopProductMap,
  useDeleteEshopProductMap,
  type EshopProductMapRow,
  type UpsertEshopProductMapPayload,
  type EshopProductMapKind,
} from "../../../lib/queries/eshopProductMap";
import { useVoucherTypes } from "../../../lib/queries/voucherTypes";
import { useMerchKinds } from "../../../lib/queries/merchKinds";
import { ApiError } from "../../../lib/api";
import { notifySuccess } from "@marekl/adminer/api";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; row: EshopProductMapRow }
  | { kind: "delete"; row: EshopProductMapRow };

interface FormState {
  eshop_product_id: string;
  kind: EshopProductMapKind;
  voucher_type_id: string;
  merch_kind_id: string;
  default_quantity: string;
}

const EMPTY_FORM: FormState = {
  eshop_product_id: "",
  kind: "voucher",
  voucher_type_id: "",
  merch_kind_id: "",
  default_quantity: "1",
};

function rowToForm(row: EshopProductMapRow): FormState {
  return {
    eshop_product_id: row.eshop_product_id,
    kind: row.kind,
    voucher_type_id: row.voucher_type_id != null ? String(row.voucher_type_id) : "",
    merch_kind_id: row.merch_kind_id != null ? String(row.merch_kind_id) : "",
    default_quantity: String(row.default_quantity),
  };
}

function formToPayload(f: FormState): UpsertEshopProductMapPayload {
  return {
    eshop_product_id: f.eshop_product_id.trim(),
    kind: f.kind,
    voucher_type_id: f.kind === "voucher" && f.voucher_type_id ? Number(f.voucher_type_id) : null,
    merch_kind_id: f.kind === "merch" && f.merch_kind_id ? Number(f.merch_kind_id) : null,
    default_quantity: Math.max(1, Number(f.default_quantity) || 1),
  };
}

function kindBadge(kind: EshopProductMapKind) {
  return kind === "voucher"
    ? <Badge variant="ok">Voucher</Badge>
    : <Badge variant="info">Merch</Badge>;
}

export function EshopProductMapCard() {
  const { data, isLoading } = useEshopProductMapList();
  const { data: voucherTypes = [] } = useVoucherTypes();
  const { data: merchKinds = [] } = useMerchKinds();
  const createMap = useCreateEshopProductMap();
  const updateMap = useUpdateEshopProductMap();
  const deleteMap = useDeleteEshopProductMap();

  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const rows = data?.items ?? [];

  function openCreate() {
    setForm(EMPTY_FORM);
    setFormError(null);
    setModal({ kind: "create" });
  }

  function openEdit(row: EshopProductMapRow) {
    setForm(rowToForm(row));
    setFormError(null);
    setModal({ kind: "edit", row });
  }

  function openDelete(row: EshopProductMapRow) {
    setModal({ kind: "delete", row });
  }

  function closeModal() {
    setModal({ kind: "closed" });
  }

  function setField<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
    setFormError(null);
  }

  function validate(): string | null {
    if (!form.eshop_product_id.trim()) return "ID produktu v e-shopu je povinné.";
    if (form.kind === "voucher" && !form.voucher_type_id) return "Typ voucheru je povinný pro druh Voucher.";
    if (form.kind === "merch" && !form.merch_kind_id) return "Druh merche je povinný pro druh Merch.";
    return null;
  }

  async function handleSave() {
    const err = validate();
    if (err) { setFormError(err); return; }
    const payload = formToPayload(form);
    try {
      if (modal.kind === "create") {
        await createMap.mutateAsync(payload);
        notifySuccess("Mapování produktu vytvořeno.");
      } else if (modal.kind === "edit") {
        await updateMap.mutateAsync({ id: modal.row.id, payload });
        notifySuccess("Mapování produktu aktualizováno.");
      }
      closeModal();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setFormError("Produkt s tímto ID v e-shopu už je namapován.");
      } else {
        setFormError("Ukládání se nezdařilo. Zkuste to znovu.");
      }
    }
  }

  async function handleDelete() {
    if (modal.kind !== "delete") return;
    try {
      await deleteMap.mutateAsync(modal.row.id);
      notifySuccess("Mapování produktu odstraněno.");
      closeModal();
    } catch {
      setFormError("Odstranění se nezdařilo.");
    }
  }

  const isSaving = createMap.isPending || updateMap.isPending;

  return (
    <>
      <Card
        header={
          <Card.Header
            title="Mapování e-shop produktů"
            subtitle="párování SKU z e-shopu s typy voucherů a merche"
            actions={
              <Button variant="primary" size="sm" onClick={openCreate}>
                <Plus size={14} aria-hidden="true" />
                Přidat
              </Button>
            }
          />
        }
        bodyPadding="none"
      >
        <Table<EshopProductMapRow>
          columns={[
            {
              key: "eshop_product_id",
              header: "ID produktu (e-shop)",
              render: (r) => <Text variant="mono">{r.eshop_product_id}</Text>,
            },
            {
              key: "kind",
              header: "Druh",
              render: (r) => kindBadge(r.kind),
            },
            {
              key: "voucher_type",
              header: "Typ / Druh",
              render: (r) => {
                if (r.kind === "voucher") {
                  if (r.voucher_type_id == null) return <Text variant="muted">—</Text>;
                  const vt = voucherTypes.find((v) => v.id === r.voucher_type_id);
                  return vt ? vt.name : <Text variant="mono">{r.voucher_type_id}</Text>;
                }
                if (r.merch_kind_id == null) return <Text variant="muted">—</Text>;
                const mk = merchKinds.find((m) => m.id === r.merch_kind_id);
                return mk ? mk.name : <Text variant="mono">{r.merch_kind_id}</Text>;
              },
            },
            {
              key: "default_quantity",
              header: "Výchozí množství",
              render: (r) => r.default_quantity,
            },
            {
              key: "actions",
              header: "",
              render: (r) => (
                <Table.Actions>
                  <Table.Action iconOnly onClick={() => openEdit(r)} aria-label="Upravit">
                    <Pencil size={14} />
                  </Table.Action>
                  <Table.Action iconOnly variant="danger" onClick={() => openDelete(r)} aria-label="Odstranit">
                    <Trash2 size={14} />
                  </Table.Action>
                </Table.Actions>
              ),
            },
          ]}
          rows={rows}
          rowKey={(r) => r.id}
          empty={
            isLoading
              ? <span>Načítání…</span>
              : <span>Žádná mapování produktů. Klikněte na „Přidat".</span>
          }
        />
      </Card>

      <Modal
        open={modal.kind === "create" || modal.kind === "edit"}
        title={modal.kind === "create" ? "Přidat mapování produktu" : "Upravit mapování produktu"}
        onClose={closeModal}
        width={460}
        footer={
          <>
            <Button variant="default" onClick={closeModal} disabled={isSaving}>Zrušit</Button>
            <Button variant="primary" onClick={handleSave} loading={isSaving} disabled={isSaving}>Uložit</Button>
          </>
        }
      >
        <FormStack>
          {formError && <Alert variant="err">{formError}</Alert>}
          <Input
            label="ID produktu v e-shopu"
            value={form.eshop_product_id}
            onChange={(e) => setField("eshop_product_id", e.target.value)}
            placeholder="např. PROD-123"
            required
          />
          <Select
            label="Druh"
            value={form.kind}
            onChange={(e) => {
              const newKind = e.target.value as EshopProductMapKind;
              setForm((f) => ({ ...f, kind: newKind, voucher_type_id: "", merch_kind_id: "" }));
              setFormError(null);
            }}
          >
            <option value="voucher">Voucher</option>
            <option value="merch">Merch</option>
          </Select>
          {form.kind === "voucher" && (
            <Select
              label="Typ voucheru"
              value={form.voucher_type_id}
              onChange={(e) => setField("voucher_type_id", e.target.value)}
            >
              <option value="">— vyberte typ —</option>
              {voucherTypes.filter((v) => v.active).map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </Select>
          )}
          {form.kind === "merch" && (
            <Select
              label="Druh merche"
              value={form.merch_kind_id}
              onChange={(e) => setField("merch_kind_id", e.target.value)}
            >
              <option value="">— vyberte druh —</option>
              {merchKinds.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </Select>
          )}
          <Input
            label="Výchozí množství"
            type="number"
            min={1}
            value={form.default_quantity}
            onChange={(e) => setField("default_quantity", e.target.value)}
          />
        </FormStack>
      </Modal>

      <Modal
        open={modal.kind === "delete"}
        title="Odstranit mapování"
        onClose={closeModal}
        width={400}
        footer={
          <>
            <Button variant="default" onClick={closeModal}>Zrušit</Button>
            <Button variant="danger" onClick={handleDelete} loading={deleteMap.isPending}>Odstranit</Button>
          </>
        }
      >
        <FormStack>
          {modal.kind === "delete" && (
            <p>Opravdu chcete odstranit mapování pro produkt <Text as="strong" variant="mono">{modal.row.eshop_product_id}</Text>?</p>
          )}
        </FormStack>
      </Modal>
    </>
  );
}
